import { css, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { BaseElement } from "./base/BaseElement.js";
import { MediaImageType } from "../types/ShopifyImageTypes.js";

const styles = css`
  .gallery-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.95);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    overflow: hidden;
  }

  .gallery-overlay.open {
    opacity: 1;
    pointer-events: all;
  }

  .gallery-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .gallery-images-wrapper {
    display: flex;
    height: 100%;
    transition: transform 0.3s ease;
    will-change: transform;
  }

  .gallery-image-item {
    min-width: 100%;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
  }

  .gallery-image {
    max-width: 95%;
    //max-height: calc(90% - 140px);
    object-fit: contain;
    opacity: 0.5;
    transform: scale(1);
    transition: opacity 0.3s ease, transform 0.3s ease;
    pointer-events: none;
  }

  .gallery-overlay.open .gallery-image {
    opacity: 0.5;
  }

  .gallery-overlay.open .gallery-image.active {
    opacity: 1;
    transform: scale(1);
    z-index: 1;
  }

  .gallery-overlay.dragging .gallery-image.active {
    transform: scale(0.95);
  }

  .gallery-close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 48px;
    height: 48px;
    background-color: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10001;
  }

  .gallery-close-button:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }

  .gallery-close-button::before,
  .gallery-close-button::after {
    content: "";
    position: absolute;
    width: 24px;
    height: 2px;
    background-color: white;
    border-radius: 1px;
  }

  .gallery-close-button::before {
    transform: rotate(45deg);
  }

  .gallery-close-button::after {
    transform: rotate(-45deg);
  }

  .gallery-nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 48px;
    background-color: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10001;
  }

  .gallery-nav-button:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-50%) scale(1.1);
  }

  .gallery-nav-button.prev {
    left: 20px;
  }

  .gallery-nav-button.next {
    right: 20px;
  }

  .gallery-nav-button.prev::before {
    content: "";
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 12px 8px 0;
    border-color: transparent white transparent transparent;
    margin-left: -4px;
  }

  .gallery-nav-button.next::before {
    content: "";
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 0 8px 12px;
    border-color: transparent transparent transparent white;
    margin-right: -4px;
  }

  .gallery-counter {
    position: absolute;
    bottom: 140px;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    font-size: 14px;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 8px 16px;
    border-radius: 20px;
    z-index: 10001;
  }

  .gallery-thumbnails {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    overflow: hidden;
    z-index: 10001;
  }

  .gallery-thumbnail-wrapper {
    width: 100%;
    overflow: hidden;
    display: flex;
    justify-content: center;
  }

  .gallery-thumbnail-container {
    display: flex;
    gap: 10px;
    align-items: center;
    transition: transform 0.3s ease;
    will-change: transform;
    overflow:scroll;
  }

  .gallery-thumbnail {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0.6;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    flex-shrink: 0;
  }

  .gallery-thumbnail:hover {
    opacity: 0.9;
    transform: scale(1.1);
  }

  .gallery-thumbnail.active {
    opacity: 1;
    border-color: white;
    transform: scale(1.15);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  }
`;

@customElement("image-gallery")
export class ImageGallery extends BaseElement {
  static override styles = styles;
  protected override useShadowRoot = true;

  @property({ type: Boolean })
  private _isOpen = false;

  @property({ type: Array })
  private _images: MediaImageType[] = [];

  @property({ type: Number })
  private _currentIndex = 0;

  private _imageTranslateX = 0;
  private _touchStartX = 0;
  private _touchStartY = 0;
  private _touchCurrentX = 0;
  private _isDragging = false;
  private _thumbnailWidth = 90; // 80px width + 10px gap
  private _baseImageTranslateX = 0; // Base position before drag

  open(images: MediaImageType[], startIndex: number = 0) {
    this._images = images;
    this._currentIndex = startIndex;
    this._isOpen = true;
    this.requestUpdate();
    // Prevent body scroll when gallery is open
    document.body.style.overflow = "hidden";
    // Focus trap
    this.addEventListener("keydown", this._handleKeyDown);
    // Update thumbnail and image positions after render
    this.updateComplete.then(() => {
      this._updateThumbnailPosition();
      this._updateImagePosition();
    });
  }

  close() {
    this._isOpen = false;
    this.requestUpdate();
    document.body.style.overflow = "";
    this.removeEventListener("keydown", this._handleKeyDown);
  }

  private _handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.close();
    } else if (e.key === "ArrowLeft") {
      this._prevImage();
    } else if (e.key === "ArrowRight") {
      this._nextImage();
    }
  };

  private _nextImage() {
    if (this._images.length > 0) {
      this._currentIndex = (this._currentIndex + 1) % this._images.length;
      this.requestUpdate();
      this._updateThumbnailPosition();
      this._updateImagePosition();
    }
  }

  private _prevImage() {
    if (this._images.length > 0) {
      this._currentIndex =
        (this._currentIndex - 1 + this._images.length) % this._images.length;
      this.requestUpdate();
      this._updateThumbnailPosition();
      this._updateImagePosition();
    }
  }

  private _selectImage(index: number) {
    if (index >= 0 && index < this._images.length) {
      this._currentIndex = index;
      this.requestUpdate();
      this._updateThumbnailPosition();
      this._updateImagePosition();
    }
  }

  private _updateImagePosition() {
    // Update image container position based on current index
    this.updateComplete.then(() => {
      const imagesWrapper = this.shadowRoot?.querySelector(
        ".gallery-images-wrapper"
      ) as HTMLElement;
      const container = this.shadowRoot?.querySelector(
        ".gallery-container"
      ) as HTMLElement;
      
      if (imagesWrapper && container) {
        const containerWidth = container.offsetWidth || window.innerWidth;
        // Move container so current image is at the start (left: 0)
        this._imageTranslateX = -this._currentIndex * containerWidth;
        imagesWrapper.style.transform = `translateX(${this._imageTranslateX}px)`;
      }
    });
  }

  private _updateThumbnailPosition() {
    // Calculate translateX to center the active thumbnail
    this.updateComplete.then(() => {
      const container = this.shadowRoot?.querySelector(
        ".gallery-thumbnail-container"
      ) as HTMLElement;
      const wrapper = this.shadowRoot?.querySelector(
        ".gallery-thumbnail-wrapper"
      ) as HTMLElement;
      
      if (container && wrapper) {
        //const wrapperWidth = wrapper.offsetWidth;
        
        // Calculate the position of the active thumbnail's center
        // Each thumbnail is 80px + 10px gap = 90px
        //const thumbnailCenter = this._currentIndex * this._thumbnailWidth + this._thumbnailWidth / 2;
        
        // Calculate how much we need to translate to center it
        // We want the thumbnail center to align with wrapper center
        //const translateX = wrapperWidth / 2 - thumbnailCenter;
        
        //container.style.transform = `translateX(${translateX}px)`;
      }
    });
  }

  private _handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
      this._isDragging = false;
      
      // Save current base position immediately
      const container = this.shadowRoot?.querySelector(
        ".gallery-container"
      ) as HTMLElement;
      if (container) {
        const containerWidth = container.offsetWidth || window.innerWidth;
        this._baseImageTranslateX = -this._currentIndex * containerWidth;
      } else {
        // Fallback if container not found
        this._baseImageTranslateX = this._imageTranslateX;
      }
    }
  };

  private _handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      this._touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;
      
      const deltaX = Math.abs(this._touchCurrentX - this._touchStartX);
      const deltaY = Math.abs(touchCurrentY - this._touchStartY);
      
      // Only start dragging if horizontal movement is greater than vertical
      if (deltaX > 10 && deltaX > deltaY) {
        if (!this._isDragging) {
          this._isDragging = true;
          // Disable transition during drag for smooth real-time movement
          const imagesWrapper = this.shadowRoot?.querySelector(
            ".gallery-images-wrapper"
          ) as HTMLElement;
          const overlay = this.shadowRoot?.querySelector(
            ".gallery-overlay"
          ) as HTMLElement;
          if (imagesWrapper) {
            imagesWrapper.style.transition = "none";
          }
          // Add dragging class to overlay for scale effect
          if (overlay) {
            overlay.classList.add("dragging");
          }
        }
        
        e.preventDefault(); // Prevent scrolling
        
        // Calculate drag offset
        const dragDeltaX = this._touchCurrentX - this._touchStartX;
        const newTranslateX = this._baseImageTranslateX + dragDeltaX;
        
        // Apply transform in real-time
        const imagesWrapper = this.shadowRoot?.querySelector(
          ".gallery-images-wrapper"
        ) as HTMLElement;
        if (imagesWrapper) {
          imagesWrapper.style.transform = `translateX(${newTranslateX}px)`;
        }
      }
    }
  };

  private _handleTouchEnd = (e: TouchEvent) => {
    if (this._isDragging) {
      const imagesWrapper = this.shadowRoot?.querySelector(
        ".gallery-images-wrapper"
      ) as HTMLElement;
      const overlay = this.shadowRoot?.querySelector(
        ".gallery-overlay"
      ) as HTMLElement;
      
      // Re-enable transition
      if (imagesWrapper) {
        imagesWrapper.style.transition = "transform 0.3s ease";
      }
      // Remove dragging class
      if (overlay) {
        overlay.classList.remove("dragging");
      }
      
      const deltaX = this._touchCurrentX - this._touchStartX;
      const swipeThreshold = 50; // Minimum distance for swipe
      
      if (Math.abs(deltaX) > swipeThreshold) {
        if (deltaX > 0) {
          // Swipe right - previous image
          this._prevImage();
        } else {
          // Swipe left - next image
          this._nextImage();
        }
      } else {
        // Snap back to current image if threshold not met
        this._updateImagePosition();
      }
      
      this._isDragging = false;
    }
  };

  private _handleOverlayClick(e: MouseEvent) {
    // Close if clicking on overlay background (not on image or buttons)
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  private _handleThumbnailClick(e: Event) {
    e.stopPropagation();
  }

  protected renderHtml(): TemplateResult {
    if (!this._isOpen || this._images.length === 0) {
      return html``;
    }

    return html`
      <div
        class="gallery-overlay ${this._isOpen ? "open" : ""}"
        @click=${this._handleOverlayClick}
      >
        <div class="gallery-container">
          ${this._images.length > 1
            ? html`
                <button
                  class="gallery-nav-button prev"
                  @click=${this._prevImage}
                ></button>
                <button
                  class="gallery-nav-button next"
                  @click=${this._nextImage}
                ></button>
              `
            : ""}
          <div 
            class="gallery-images-wrapper"
            @touchstart=${this._handleTouchStart}
            @touchmove=${this._handleTouchMove}
            @touchend=${this._handleTouchEnd}
          >
            ${this._images.map(
              (image, index) => html`
                <div class="gallery-image-item">
                  <img
                    class="gallery-image ${index === this._currentIndex ? "active" : ""}"
                    src="${image.main?.src || ""}"
                    srcset="${image.main?.srcset || ""}"
                    alt="${image.alt || ""}"
                    loading="${index === this._currentIndex || index === this._currentIndex + 1 || index === this._currentIndex - 1 ? "eager" : "lazy"}"
                  />
                </div>
              `
            )}
          </div>
          ${this._images.length > 1
            ? html`
                <div class="gallery-counter">
                  ${this._currentIndex + 1} / ${this._images.length}
                </div>
              `
            : ""}
          <button
            class="gallery-close-button"
            @click=${this.close}
            aria-label="Close gallery"
          ></button>
          ${this._images.length > 1
            ? html`
                <div class="gallery-thumbnails" @click=${this._handleThumbnailClick}>
                  <div class="gallery-thumbnail-wrapper">
                    <div class="gallery-thumbnail-container">
                      ${this._images.map(
                        (image, index) => html`
                          <img
                            class="gallery-thumbnail ${index === this._currentIndex
                              ? "active"
                              : ""}"
                            src="${image.thumb?.src || image.main?.src || ""}"
                            srcset="${image.thumb?.srcset || ""}"
                            alt="${image.alt || `Thumbnail ${index + 1}`}"
                            @click=${() => this._selectImage(index)}
                            loading="lazy"
                          />
                        `
                      )}
                    </div>
                  </div>
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }
}

